import React, { useRef, useState } from 'react';
import { isPseudoVendorName } from '../../utils/poSendStatus';
import { SupplierDetailsCard, type SupplierDetailsCardLine } from './SupplierDetailsCard';

/**
 * The Supplier column of one quotation line.
 *
 * Extracted from QuotationDetailPage when the count badge was added — the page
 * is a god file under a line ceiling (TECH_DEBT #26), so the slice being touched
 * comes out and the page keeps a delegating call.
 *
 * Owner decisions 2026-09-24: JP was confused by the count badge this cell used
 * to carry ("West code x3" read as three suppliers for THIS item, when the
 * count was really how many rows the merged catalogue+supplier lookup panel
 * would open with across the whole search). The badge, and the click that used
 * to open that panel (`ItemLookupPanel` — still opened by the row's magnifier,
 * unchanged), are both gone from this cell. Clicking it now expands a
 * read-only `SupplierDetailsCard` for the one row this line is actually priced
 * from — nothing to compare, nothing to pick, so there is nothing left for a
 * count to describe.
 *
 * The open/anchor state now lives HERE, not on the page: unlike the shared,
 * heavy `ItemLookupPanel` (one search, one fetch, one instance for the whole
 * table), this card is a per-row, read-only popover — the same
 * self-contained shape `components/ModuleInfoButton.tsx` already uses — so
 * there is no "one open at a time" invariant for the page to keep.
 */
interface SupplierCellProps {
  /** The vendor currently on the line, if one has been chosen. */
  selectedVendor?: string | null;
  /** How this line was priced, when no catalogue row backs it (see `SupplierDetailsCard`). */
  pricingSource?: string | null;
  /** The unit the line is currently quoted in. */
  uom?: string | null;
  /** `utils/pricing.unitCostTtd(item, ttdToUsdRate)`, resolved by the caller — read, never re-derived here. */
  unitCostTtd?: number | null;
  /** D86: the date the line stored for its source price (see `SupplierDetailsCardLine.priceDate`). */
  priceDate?: string | null;
  /** `pricingDetails.masterCatalogueId` off the line's DTO — which catalogue row prices this line, if any. */
  masterCatalogueId?: string | null;
  /** N7 (owner decision, 2026-09-25): the quotation this line belongs to, and its own position in `items` — what the read-only details card's own endpoint addresses a line by. */
  quotationId: string;
  itemIndex: number;
  /**
   * R11 (owner decisions 2026-09-25): `QuotationGridRowCells` clones every
   * plain `<div>` cell in a row to merge its `rowTint` (the RFQ-unit-mismatch
   * amber background) and `rowAnchor` — but this cell is a COMPONENT, not a
   * `<div>`, so the cloned className/title landed on `<SupplierCell>`'s own
   * props and this cell silently dropped them, leaving the Supplier column
   * the one cell in a tinted row that stayed white. Applied to the cell's own
   * root below, merged with its own classes, same as every other cell in the
   * row already does.
   */
  className?: string;
  title?: string;
}

export const SupplierCell: React.FC<SupplierCellProps> = ({
  selectedVendor,
  pricingSource,
  uom,
  unitCostTtd,
  priceDate,
  masterCatalogueId,
  quotationId,
  itemIndex,
  className,
  title,
}) => {
  // USC-BUG-0021: the server stores 'Master Catalogue' / 'Unknown Vendor' /
  // 'Price List' / 'Client' when no vendor is attributed to the line. Those
  // are not supplier names, so the line reads as unpicked, not as a supplier.
  const shownVendor = isPseudoVendorName(selectedVendor) ? null : selectedVendor;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const line: SupplierDetailsCardLine = { pricingSource, selectedVendor, uom, unitCostTtd, priceDate };

  return (
    // min-w-0/overflow-hidden as on the other text cells: without them a grid
    // item refuses to shrink below its content, so a long supplier name would
    // push the chevron out of the cell instead of truncating.
    <div
      className={`box-border flex flex-row justify-center items-center py-[4px] px-[8px] gap-[10px] bg-white border-r border-b border-solid border-[#D7DBD9] relative min-w-0 overflow-hidden ${className || ''}`.trim()}
      title={title}
    >
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDetailsOpen((v) => !v);
        }}
        aria-expanded={detailsOpen}
        className="w-full min-w-0 text-left px-0 py-0 gap-1 font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900 hover:bg-gray-50 rounded transition-colors flex items-center justify-between"
      >
        {/* This cell no longer picks (the Item Name magnifier does), so the
            empty state states a fact, not an action (client 2026-09-26). */}
        <span
          className={`truncate ${shownVendor ? '' : 'text-slate-500 italic'}`}
          title={shownVendor || 'No supplier on this line yet — pick one with the search icon in the Item Name cell'}
        >
          {shownVendor || 'No supplier — use the search icon in Item Name'}
        </span>

        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transition-transform flex-shrink-0 ${detailsOpen ? 'rotate-180' : ''}`}
          style={{ pointerEvents: 'none' }}
        >
          <path d="M10.0001 12.4788C9.88897 12.4788 9.7848 12.4615 9.68758 12.4268C9.59036 12.392 9.50008 12.333 9.41675 12.2497L5.58341 8.41634C5.43064 8.26356 5.35425 8.06912 5.35425 7.83301C5.35425 7.5969 5.43064 7.40245 5.58341 7.24967C5.73619 7.0969 5.93064 7.02051 6.16675 7.02051C6.40286 7.02051 6.5973 7.0969 6.75008 7.24967L10.0001 10.4997L13.2501 7.24967C13.4029 7.0969 13.5973 7.02051 13.8334 7.02051C14.0695 7.02051 14.264 7.0969 14.4167 7.24967C14.5695 7.40245 14.6459 7.5969 14.6459 7.83301C14.6459 8.06912 14.5695 8.26356 14.4167 8.41634L10.5834 12.2497C10.5001 12.333 10.4098 12.392 10.3126 12.4268C10.2154 12.4615 10.1112 12.4788 10.0001 12.4788Z" fill="#222222" />
        </svg>
      </button>

      <SupplierDetailsCard
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        anchorRef={buttonRef}
        masterCatalogueId={masterCatalogueId}
        quotationId={quotationId}
        itemIndex={itemIndex}
        line={line}
      />
    </div>
  );
};

export default SupplierCell;
